import "./BulkRequestFilterForm.scss";
import { Checkbox, Flex, Switch, Typography } from "antd";
import { CurrencyInputNumber } from "@vyne-shared/components/Payment/Components/Amount/CurrencyInputNumber";
import { TablePopoverFooterButtons } from "molecules/filters/TableFilter/TablePopoverFooterButtons";
import { balanceAges, balanceMap } from "../../balanceMap";
import { useFeatureFlag } from "utils/useFeatureFlag";
import type { BulkRequestFilterAction, BulkRequestFilterState } from "./useBulkRequestFilter";

const { Title } = Typography;

/** Properties for bulk request filtering form. */
interface BulkRequestFilterFormProps extends BulkRequestFilterFormSectionProps {
    /** Callback that is invoked when user elects to close the form. */
    readonly onClose: () => void;
}

interface BulkRequestFilterFormSectionProps {
    /** Current filter state. */
    readonly state: BulkRequestFilterState;

    /** Dispatcher used to change the filter state. */
    readonly dispatch: React.Dispatch<BulkRequestFilterAction>;
}

/** Bulk request filtering form, offering age filters and minimum balance filter. */
export const BulkRequestFilterForm = ({ state, dispatch, onClose }: BulkRequestFilterFormProps) => {
    const paymentPlanSwitch = !!useFeatureFlag("payment-plan-switch", true);

    return (
        <Flex className="bulk-request-filter-form" vertical={true} gap="small">
            <BalanceAgesFilter state={state} dispatch={dispatch} />
            <OutstandingClaimsFilter state={state} dispatch={dispatch} />
            {paymentPlanSwitch && <ActivePaymentPlansFilter state={state} dispatch={dispatch} />}
            <BalanceFilter state={state} dispatch={dispatch} />
            <TablePopoverFooterButtons resetFilters={() => dispatch({ type: "ResetAction" })} onClose={onClose} />
        </Flex>
    );
};

const BalanceAgesFilter = ({ state, dispatch }: BulkRequestFilterFormSectionProps) => (
    <>
        <header>
            <Title level={3} className="filter-past-due-header">
                Past Due
            </Title>
        </header>
        {balanceAges.map((age, idx) => (
            <Checkbox
                // eslint-disable-next-line @eslint-react/no-array-index-key -- data is constant so we are safe
                key={idx}
                checked={state.agings.has(age)}
                onChange={(e) => dispatch({ type: "AgingAction", index: idx, value: e.target.checked })}
            >
                {balanceMap[age].title}
            </Checkbox>
        ))}
    </>
);

const OutstandingClaimsFilter = ({ state, dispatch }: BulkRequestFilterFormSectionProps) => (
    <>
        <header>
            <Title level={3} className="filter-balance-header">
                Claims
            </Title>
        </header>
        <div className="filter-outstanding-claims-wrapper">
            <Switch
                id="include-outstanding-claims-switch"
                checked={state.includeOutstandingClaims}
                onChange={(checked) => dispatch({ type: "IncludeOutstandingClaimsAction", value: checked })}
            />
            <label htmlFor="include-outstanding-claims-switch">Include accounts with outstanding claims</label>
        </div>
    </>
);

const ActivePaymentPlansFilter = ({ state, dispatch }: BulkRequestFilterFormSectionProps) => (
    <>
        <header>
            <Title level={3} className="filter-balance-header">
                Payment Plans
            </Title>
        </header>
        <div className="filter-outstanding-claims-wrapper">
            <Switch
                id="include-active-payment-plans-switch"
                checked={state.includeActivePaymentPlans}
                onChange={(checked) => dispatch({ type: "IncludeActivePaymentPlansAction", value: checked })}
            />
            <label htmlFor="include-active-payment-plans-switch">Include accounts with active payment plans</label>
        </div>
    </>
);

const BalanceFilter = ({ state, dispatch }: BulkRequestFilterFormSectionProps) => (
    <>
        <header>
            <Title level={3} className="filter-balance-header">
                Balance
            </Title>
        </header>
        <div className="filter-amount-greater-than">
            <Checkbox
                checked={state.minBalanceEnabled}
                onChange={(e) => dispatch({ type: "MinBalanceEnabledAction", value: e.target.checked })}
            >
                Amount greater than...
            </Checkbox>
            <CurrencyInputNumber
                aria-label="Minimum Balance"
                disabled={!state.minBalanceEnabled}
                value={state.minBalance}
                onChange={(value) => dispatch({ type: "MinBalanceAction", value })}
                onBlur={() => dispatch({ type: "MinBalanceBlurAction" })}
                style={{ float: "right", width: "10em" }}
            />
        </div>
    </>
);
